/**
 * A same-tab, one-time UI acknowledgement for a completed account deletion.
 *
 * The deletion response invalidates the authenticated application immediately.
 * Its truthful confirmation therefore belongs on the public login route, not in
 * the guarded account page that is about to unmount.  This marker intentionally
 * carries no identity, token, email, or server state.
 */
export const ACCOUNT_DELETION_NOTICE_STORAGE_KEY =
  "bw:account-deletion-notice:v1";

const ACCOUNT_DELETION_NOTICE_VALUE = "deleted";

function browserSessionStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Record the generic acknowledgement before immediate local sign-out. */
export function markAccountDeletionNotice(): void {
  try {
    browserSessionStorage()?.setItem(
      ACCOUNT_DELETION_NOTICE_STORAGE_KEY,
      ACCOUNT_DELETION_NOTICE_VALUE,
    );
  } catch {
    // Storage may be unavailable in privacy-restricted browser contexts. The
    // deletion and immediate sign-out remain authoritative without this UI aid.
  }
}

/** Consume the acknowledgement once; it must never become durable UI state. */
export function consumeAccountDeletionNotice(): boolean {
  const storage = browserSessionStorage();
  if (!storage) return false;
  try {
    const marked =
      storage.getItem(ACCOUNT_DELETION_NOTICE_STORAGE_KEY) ===
      ACCOUNT_DELETION_NOTICE_VALUE;
    storage.removeItem(ACCOUNT_DELETION_NOTICE_STORAGE_KEY);
    return marked;
  } catch {
    return false;
  }
}
